import {
  action,
  computed,
  flow,
  flowResult,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from "mobx";
import { getLendings } from "../../../api/ApiManager";
import {
  ILending,
  LendingsOwnershipType,
} from "../../../entities/Lending/LendingTypes";
import BusyStore from "../../../stores/common/BusyStore";
import { minOffsetToFetch } from "../../../utils/AppConstants";
import { handleError } from "../../../utils/handleError";
import raceGuard from "../../../utils/raceGuard";
import LendingsFilterStore from "./LendingsFilterStore";

class LendingsPageStore extends BusyStore {
  fetchedOffset = minOffsetToFetch;
  hasMore = true;
  isFetchingMore = false;

  lendingItemsMap: Map<string, ILending> = new Map();

  filterDisposer: IReactionDisposer;
  collectionsDisposer: IReactionDisposer;

  constructor() {
    super();

    makeObservable<LendingsPageStore, "loadItemServicesFlow">(this, {
      lendingItemsMap: observable,
      fetchedOffset: observable,
      hasMore: observable,
      isFetchingMore: observable,

      lendingItems: computed,
      lendingFilter: computed,

      setHasMore: action,
      setFetchedOffset: action,
      loadItemServices: action,

      loadItemServicesFlow: flow,

      filterDisposer: false,
      collectionsDisposer: false,
      dispose: false,
    });

    this.onLoadingStart();

    this.filterDisposer = reaction(
      () => ({
        status: this.lendingFilter.statusStore.selectedOption,
      }),
      async ({ status }) => {
        try {
          this.lendingFilter.setCollectionsLoading(true);
          this.lendingFilter.setBorrowerAddress(null);
          this.lendingFilter.setLenderAddress(null);
          this.lendingFilter.ownershipStore.setSelectedOption(
            LendingsOwnershipType.all
          );
          this.lendingFilter.collectionsStore.setSelectedOption([]);

          await this.loadItemServices();
          await this.lendingFilter.fetchCollections(status);
        } catch (error: unknown) {
          // TODO: nik add handleError
          console.log("nik filterDisposer error", error);
        } finally {
          this.lendingFilter.setCollectionsLoading(false);
        }
      },
      { fireImmediately: false }
    );

    this.collectionsDisposer = reaction(
      () => ({
        collections: this.lendingFilter.collectionsStore.selectedOption,
        sort: this.lendingFilter.sortStore.selectedOption,
        search: this.lendingFilter.searchText,
        ownership: this.lendingFilter.ownershipStore.selectedOption,
        durations: this.lendingFilter.durationsStore.selectedOption,
      }),
      async ({ collections }) => {
        await this.lendingFilter.fetchDurations(
          this.lendingFilter.statusStore.selectedOption,
          collections
        );
        await this.loadItemServices();
      },
      { fireImmediately: false }
    );
  }

  setHasMore(status: boolean) {
    this.hasMore = status;
  }

  setFetchedOffset(offset: number) {
    this.fetchedOffset = offset;
  }

  get lendingFilter(): LendingsFilterStore {
    const store = new LendingsFilterStore();
    return store;
  }

  get lendingItems(): ILending[] {
    return [...this.lendingItemsMap.values()];
  }

  readonly loadItemServices = raceGuard(async () =>
    flowResult(this.loadItemServicesFlow())
  );

  private *loadItemServicesFlow() {
    if (this.fetchedOffset > minOffsetToFetch) {
      this.isFetchingMore = true;
    } else {
      this.onLoadingStart();
    }

    try {
      const lendings: ILending[] = yield getLendings(
        this.fetchedOffset,
        this.lendingFilter.statusStore.selectedOption,
        this.lendingFilter.sortStore.selectedOption,
        this.lendingFilter.collectionsStore.selectedOption,
        this.lendingFilter.searchText,
        this.lendingFilter.borrowerAddress,
        this.lendingFilter.lenderAddress,
        this.lendingFilter.durationsStore.selectedOption
      );

      if (lendings.length === 0) {
        if (this.fetchedOffset === minOffsetToFetch) {
          this.lendingItemsMap.clear();
        }
        this.hasMore = false;
        return;
      }

      if (this.fetchedOffset === minOffsetToFetch) {
        this.lendingItemsMap.clear();
      }

      for (const lending of lendings) {
        this.lendingItemsMap.set(lending.address, lending);
      }

      this.hasMore = lendings.length >= minOffsetToFetch;
    } catch (error: unknown) {
      handleError(error, "LendingsPageStore loadItemServicesFlow error");
    } finally {
      this.isFetchingMore = false;
      this.onLoadingFinished();
    }
  }

  dispose() {
    this.filterDisposer();
    this.collectionsDisposer();
  }
}

export default LendingsPageStore;
